import { Directive, inject, signal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  FilterCollectionType,
  GroupingCollectionType,
  PeriodCollectionType,
  StateControlCollection,
} from "@bitwarden/web-vault/app/services/store/state-control.abstraction";

import { FilterControl } from "./controls/filter.control";
import { GroupControl } from "./controls/group.control";
import { PeriodController } from "./controls/period.control";
import { CalculateTransactionGeneratedCollection } from "./calculate-transaction.generated.collection";

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  Observable,
  skipUntil,
  Subscription,
} from "rxjs";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { WebWorkerQueue, WEB_WORKER_QUEUE } from "../../web-worker/WebWorkerQueue";
import { FilteredTransactionGeneratedCollection } from "./filtered-transaction.generated.collection";
import { CalculatedStoreAbstraction } from "@bitwarden/web-vault/app/services/store/calculated.store.abstraction";
import { FilteredAccountGeneratedCollection } from "./filtered-account.generated.collection";

import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";

import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { enrichTransactionDate } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { RevaluationTransactionsGeneratedCollection } from "@bitwarden/web-vault/app/services/store/calculation-base/generated-transactions/revaluation-transactions.generated.collection";
import { GranularityProperty } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";

export interface CalculatedStoreState {
  status: "initialise" | "idle" | "updatingControl" | "updatingCalculation" | "completed" | "error";
}

interface CalculatedStorePage {
  isDirtyFilteringOptions: boolean;
}

/** The inputs a completed balance calculation ran on — see `completedCalculation`. */
export interface CompletedCalculation {
  /** `calculateTransaction.collection()` as it was when the balances were computed. */
  readonly transactions: readonly TransactionView[];
  /** `period.selected()` the balances were computed as of. */
  readonly period: PeriodType;
}

/** Use when re-running after skipped refreshes so filter options stay in sync with the collection. */
const CalculationRerunPage: CalculatedStorePage = {
  isDirtyFilteringOptions: true,
};

/**
 * Decide whether refreshState() needs to re-run itself because reval was computed at a
 * different granularity than the one the cycle ultimately settles on for display.
 *
 * `revalGranularity` is resolved from real-only transactions, before revals exist, and is what
 * the reval engine was actually called with. `displayGranularity` is resolved afterward from the
 * real+reval transaction set and is what the UI (chart, granularity dropdown) reads. These two
 * resolutions are normally expected to agree, but nothing guarantees it — if they disagree, reval
 * ran at the wrong granularity and the transaction list will be missing whatever periods only
 * exist at the correct one (e.g. showing far fewer weeks than the balance chart plots).
 *
 * `revalGranularity` is `null` when reval didn't run at all this cycle (see `applyRevals()`), in
 * which case there's nothing to reconcile.
 */
export function shouldRetryForGranularityMismatch(
  revalGranularity: GranularityProperty | null,
  displayGranularity: GranularityProperty,
  retryDepth: number,
  maxRetries: number,
): boolean {
  return (
    revalGranularity !== null && displayGranularity !== revalGranularity && retryDepth < maxRetries
  );
}

@Directive()
export abstract class CalculationBaseStoreService
  extends CalculatedStoreAbstraction
  implements
    StateControlCollection<FilterCollectionType>,
    StateControlCollection<GroupingCollectionType>,
    StateControlCollection<PeriodCollectionType>
{
  /** Injected Services **/
  protected log: LogService = inject(LogService);
  protected appState: AppStateStoreService = inject(AppStateStoreService);
  protected storeState$: BehaviorSubject<CalculatedStoreState> = new BehaviorSubject({
    status: "idle",
  });

  /**
   * Emits every state transition. Part 2 consumers: note that `completed` is immediately
   * followed by `idle` as consecutive microtasks within the same event-loop turn (see
   * `updateCalculation`). Subscribe before triggering a calculation, or use
   * {@link isCalculationReady$} to check readiness instead.
   */
  get calculationState$(): Observable<CalculatedStoreState> {
    return this.storeState$.asObservable();
  }

  /** Number of times a control refresh was skipped because the store was not idle. Default 0. */
  private _skippedCalculationCount$ = new BehaviorSubject(0);
  /** True after a balance calculation cycle ends (success OR error). Default false. */
  private _isCalculationComplete$ = new BehaviorSubject(false);
  /**
   * null = cycle in progress or never started; true = last cycle succeeded; false = last cycle errored.
   * Reset to null at the start of each cycle in refreshState().
   */
  private _lastCycleSucceeded$ = new BehaviorSubject<boolean | null>(null);

  /**
   * Emits `true` after each calculation cycle ends (success or error), and is reset to `false`
   * at the start of the next cycle. A subscriber that arrives after cycle N ends but before
   * cycle N+1 starts will see an immediate `true` from the BehaviorSubject replay — that `true`
   * represents cycle N, not N+1. To wait specifically for the next cycle to finish, subscribe
   * to `calculationState$` instead and filter for `completed` or `idle`.
   *
   * **Not a success gate.** To act only on a successful cycle, use {@link isCalculationSuccessful$}.
   */
  get isCalculationReady$(): Observable<boolean> {
    return this._isCalculationComplete$.asObservable();
  }

  /**
   * Emits `true` after each *successful* calculation cycle and replays the most recent value.
   * Filters out error cycles and mid-cycle nulls, so `firstValueFrom(isCalculationSuccessful$)`
   * resolves immediately if the last cycle succeeded, or waits for the next successful one.
   */
  get isCalculationSuccessful$(): Observable<boolean> {
    return this._lastCycleSucceeded$.asObservable().pipe(filter((v): v is true => v === true));
  }

  /**
   * Emits `true` only when the store is both idle AND the last cycle was successful.
   * Stricter than {@link isCalculationSuccessful$}: also requires `idle` status, so
   * resolution cannot race with a new cycle resetting `_lastCycleSucceeded$` to `null`
   * in `refreshState` before data reads complete.
   * Use this (not `isCalculationSuccessful$`) when reading collections immediately after await.
   */
  get isIdleAfterSuccess$(): Observable<true> {
    return combineLatest([this.storeState$, this._lastCycleSucceeded$]).pipe(
      filter(([state, succeeded]) => state.status === "idle" && succeeded === true),
      map(() => true as const),
      distinctUntilChanged(),
    );
  }

  private readonly _completedCalculation = signal<CompletedCalculation | null>(null);

  /**
   * The transactions and period that the balance collections' current values were computed
   * from, or null while those collections are being recalculated, after a calculation that
   * failed, and before the first one. Set only once every balance collection has been written.
   *
   * Read this rather than the live `calculateTransaction.collection()` / `period.selected()`
   * whenever transactions are combined with a balance: the live signals move on as soon as the
   * next cycle starts (synchronously, in refreshState), but the balances only follow once the
   * web worker resolves, so reading the live pair can mix two cycles (#5383).
   */
  readonly completedCalculation = this._completedCalculation.asReadonly();

  /** Snapshot of whether the last calculation cycle has finished; drives skipped-refresh retries with {@link _skippedCalculationCount$}. */
  protected get isCalculationComplete(): boolean {
    return this._isCalculationComplete$.value;
  }

  protected userStore: UserStoreService = inject(UserStoreService);
  protected transactionStoreService: TransactionStoreService = inject(TransactionStoreService);

  protected readonly webWorkerQueue = inject(WEB_WORKER_QUEUE);
  referenceData = this.transactionStoreService.referenceData;

  /** Define the state control ,for component to use and update **/
  period = new PeriodController();
  filters = new FilterControl();
  groupings = new GroupControl();

  /** This contains all the transaction with the filter applied to be used for balance calculation **/
  calculateTransaction = new CalculateTransactionGeneratedCollection();

  /** This contains all the transaction with the filters and period applied on the collection. to be use for displaying the list **/
  filteredTransactions = new FilteredTransactionGeneratedCollection();
  filteredAccounts = new FilteredAccountGeneratedCollection();

  /** Revaluation Transactions **/
  revaluationTransactions = new RevaluationTransactionsGeneratedCollection();

  /** Trigger subscriptions **/
  private subscriptions: Subscription[] = [];
  private previousPeriod: PeriodType;

  /** Bumped by clearStore(). clearStore() can't cancel an in-flight cycle, so a cycle takes a
   * staleCheck() before its first await and checks it after each one: a cycle from before the
   * clear (e.g. the previous user's, on logout) must not write its transactions, completion or
   * success into the cleared store, which would resolve isCalculationReady$ / isIdleAfterSuccess$
   * waiters before the next session's own first cycle. The balance collections guard their own
   * writes the same way (CalculatedBalanceCollection). Same pattern as ScenarioStoreService. */
  private storeEpoch = 0;

  clearStore(): void {
    this.storeEpoch++;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];

    /** Balance Collection **/
    this.filteredAccounts.clear();
    this.filteredTransactions.clear();
    this.calculateTransaction.clear();
    // Also drops a reval still awaiting its worker (see its clear()): MonthlySummaryService reads
    // this collection directly, so the next user mustn't see the previous user's revals.
    this.revaluationTransactions.clear();

    /** Controls **/
    this.filters.clear();
    this.groupings.clear();
    this.period.clear();

    this._skippedCalculationCount$.next(0);
    this._isCalculationComplete$.next(false);
    this._lastCycleSucceeded$.next(null);
    this._completedCalculation.set(null);
    this.clearBalanceCollections();
    // A clear can land mid-cycle. That cycle no longer writes anything (see storeEpoch), including
    // the idle its .finally used to set, so reset it here: otherwise the next session's first
    // refreshState() would find the store busy and be skipped, with nothing left to retry it.
    if (this.storeState$.value.status !== "idle") {
      this.storeState$.next({ status: "idle" });
    }
  }

  /** Clears the subclass's balance collections. Called by clearStore() before it puts the status
   * back to idle, so nothing reacting to that idle can read the previous session's balances. */
  protected abstract clearBalanceCollections(): void;

  /** Returns a check that turns true once clearStore() has run since this call. */
  private staleCheck(): () => boolean {
    const epoch = this.storeEpoch;
    return () => epoch !== this.storeEpoch;
  }

  initialize(): void {
    this.log.info("Initializing CalculationStoreService");
    this.initialiseCollectionListener();
  }

  private async onEnrichedAlignedCollectionChange(): Promise<void> {
    this.log.info("Change Detected **** Regenerating collection list ****");
    this.log.info("onEnrichedAlignedCollectionChange triggered");

    await this.refreshState({
      isDirtyFilteringOptions: true,
    });
  }

  private async onFilterChange(): Promise<void> {
    this.log.info("Change Detected **** Filters ****");
    await this.refreshState({
      isDirtyFilteringOptions: false,
    });
  }

  private async onPeriodChange(): Promise<void> {
    this.log.info("Change Detected **** Period ****");
    await this.refreshState({
      isDirtyFilteringOptions: false,
    });
  }

  private async onGroupingChange(): Promise<void> {
    this.log.info("Change Detected **** Grouping ****");
    await this.refreshState({
      isDirtyFilteringOptions: false,
    });
  }

  /**
   * Bounded number of times refreshState() will re-run itself within a single triggering event
   * when reval was computed at a different granularity than the one the cycle settles on for
   * display (see the granularity-mismatch check near the end of this method). One retry is
   * enough to let reval regenerate at the now-settled granularity; if it still disagrees after
   * that, proceed anyway rather than risk looping indefinitely.
   */
  private static readonly MAX_GRANULARITY_RETRIES = 1;

  private async refreshState(page: CalculatedStorePage, granularityRetryDepth = 0): Promise<void> {
    if (this.storeState$.value.status === "idle") {
      // Taken before the cycle's first await, so both the reval check below and the catch can
      // tell whether clearStore() ran while this cycle was in flight.
      const isStale = this.staleCheck();
      try {
        this._isCalculationComplete$.next(false);
        this._lastCycleSucceeded$.next(null);

        this.storeState$.next({ status: "updatingControl" });
        this.log.info("Starting control update");

        let enrichAlignedCollection = this.calculateTransaction.dependentStore.getCollection();

        // Recompute period + granularity from the filtered view *before* reval runs, using the same
        // filters applyFiltersOnTransaction() will apply below. Without this, a filter change (e.g.
        // clicking into one account) would reval using the granularity left over from the previous
        // (differently-filtered) cycle, lagging a full cycle behind what the UI is about to show.
        // updateOptionAndSelectionFromPeriod() already preserves a manual granularity pick as long as
        // it's still a valid option for the new period, so this doesn't discard user selections.
        // On the very first-ever cycle filters.selected() is still null (initialiseFilteringOptions()
        // below hasn't run yet), so applyFiltersOnTransaction() would return an empty list and
        // collapse the period to today — fall back to the unfiltered collection instead. On this
        // first cycle the two sets are equal in practice (initialiseFilteringOptions() seeds an
        // all-inclusive default selection), and where they can
        // diverge — applyFiltersOnTransaction() also requires a category intersection, so a txn with no
        // c_categories is dropped — the granularity-mismatch retry below still corrects it, degrading to
        // the old double-run rather than a wrong result. Doing this here is what lets reval run once at
        // the real display granularity instead of at the initial "day" and being thrown away, which
        // doubled first-load reval time (~17–20s per store). See #5497.
        const preRevalTransactions = this.filters.selected()
          ? this.filters.applyFiltersOnTransaction(enrichAlignedCollection)
          : enrichAlignedCollection;
        this.period.updateOptionAndSelectionFromTransactions(preRevalTransactions);
        this.groupings.updateOptionAndSelectionFromPeriod(this.period.selected());

        // add the reval transactions to the collection if there are no filters on categories or classifications
        // TODO: see comment on revaluateTransactions function for more details for future improvements
        let revalGranularity: GranularityProperty | null = null;
        if (this.applyRevals()) {
          revalGranularity = this.groupings.selected().granularity;

          await this.revaluateTransactions(enrichAlignedCollection, revalGranularity);
          if (isStale()) {
            // Cleared during the reval: stop before this cycle writes the previous session's
            // transactions or moves the (reset) status on into a calculation.
            this.log.info("Calculation cycle outlived a store clear — discarding it");
            return;
          }

          if (this.revaluationTransactions.collection()?.length > 0) {
            enrichAlignedCollection = enrichAlignedCollection.concat(
              this.revaluationTransactions.collection(),
            );
          }
        }

        /** Reset the filtering if the transaction list change **/
        if (page.isDirtyFilteringOptions) {
          this.log.info("--- initialiseFilteringOptions from enrich collection");
          this.initialiseFilteringOptions(enrichAlignedCollection);
        }

        this.log.info("--- Applying current filter to the enrich collection ");
        const filteredTransactionForCalculation =
          this.filters.applyFiltersOnTransaction(enrichAlignedCollection);
        filteredTransactionForCalculation.sort(sortTransactionViews);
        this.calculateTransaction.update(filteredTransactionForCalculation);

        /** Refreshing the filtered Account List **/
        this.filteredAccounts.update(
          this.filters.applyFiltersOnAccounts(this.userStore.enrichAccounts.collection()),
        );

        this.log.info("--- update Period option and selection ");
        this.period.updateOptionAndSelectionFromTransactions(filteredTransactionForCalculation);

        this.log.info("--- update grouping option and selection");
        this.groupings.updateOptionAndSelectionFromPeriod(this.period.selected());

        // See shouldRetryForGranularityMismatch() doc comment for why this check exists.
        if (
          shouldRetryForGranularityMismatch(
            revalGranularity,
            this.groupings.selected().granularity,
            granularityRetryDepth,
            CalculationBaseStoreService.MAX_GRANULARITY_RETRIES,
          )
        ) {
          this.log.info(
            `--- granularity mismatch after reval (reval=${revalGranularity}, display=${this.groupings.selected().granularity}) — re-running cycle`,
          );
          this.storeState$.next({ status: "idle" });
          await this.refreshState(page, granularityRetryDepth + 1);
          return;
        }

        this.storeState$.next({ status: "updatingCalculation" });

        const skipped = this._skippedCalculationCount$.value;
        if (skipped > 0) {
          this._skippedCalculationCount$.next(0);
        }
      } catch (error) {
        // Fail the cycle the way updateCalculation() does. Without this a throw (e.g. the
        // revaluation worker rejecting) leaves the store in "updatingControl" forever: every later
        // refresh is skipped as busy, and the error retry in initialiseCollectionListener() never
        // fires because the cycle is never marked complete (#5797).
        this.log.error(error);
        if (isStale()) {
          // clearStore() already reset the status; don't mark the next session's store complete.
          return;
        }
        this.storeState$.next({ status: "error" });
        this._lastCycleSucceeded$.next(false);
        this.storeState$.next({ status: "idle" });
        this._isCalculationComplete$.next(true);
      }
    } else {
      this.log.info(
        `Calculation control refresh skipped — store busy (status: ${this.storeState$.value.status})`,
      );
      this._skippedCalculationCount$.next(this._skippedCalculationCount$.value + 1);
    }
  }

  private initialiseCollectionListener(): void {
    this.subscriptions.push(
      this.storeState$
        .pipe(
          distinctUntilKeyChanged("status"),
          filter((x) => x.status === "updatingCalculation"),
        )
        .subscribe(this.updateCalculation.bind(this)),
    );

    const enrichedAlignedCollection$ = this.calculateTransaction.dependentStore
      .getObservable()
      .pipe(
        // getObservable() already drops nulls.
        distinctUntilChanged((previous, current) => {
          return previous === current || (previous.length === 0 && current.length === 0);
        }),
      );

    this.subscriptions.push(
      enrichedAlignedCollection$.subscribe(this.onEnrichedAlignedCollectionChange.bind(this)),
    );

    this.subscriptions.push(
      this.filters.selected$
        .pipe(
          filter((x) => !!x),
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          }),
          skipUntil(enrichedAlignedCollection$),
        )
        .subscribe(this.onFilterChange.bind(this)),
    );

    this.subscriptions.push(
      this.period.selected$
        .pipe(
          filter((x) => !!x),
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          }),
          skipUntil(enrichedAlignedCollection$),
          skipUntil(this.filters.selected$),
        )
        .subscribe(this.onPeriodChange.bind(this)),
    );

    this.subscriptions.push(
      this.groupings.selected$
        .pipe(
          filter((x) => !!x),
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          }),
          skipUntil(enrichedAlignedCollection$),
          skipUntil(this.period.selected$),
        )
        .subscribe(this.onGroupingChange.bind(this)),
    );

    // Include _lastCycleSucceeded$ so that a successful cycle following an error cycle
    // triggers the retry immediately. After errors, allow up to MAX_ERROR_RETRIES retries
    // before giving up — prevents infinite busy-loops while still self-healing transient
    // failures. A successful cycle resets the counter so future error sequences start fresh.
    // Note: errorRetryCount is local to this closure. If teardown() unsubscribes and
    // initialize() creates a new subscription, a fresh closure with errorRetryCount=0 is
    // created automatically — no explicit reset needed on reinitialize.
    const MAX_ERROR_RETRIES = 3;
    let errorRetryCount = 0;
    this.subscriptions.push(
      combineLatest([
        this._skippedCalculationCount$,
        this._isCalculationComplete$,
        this._lastCycleSucceeded$,
      ]).subscribe(([skippedCount, isCalculationComplete, lastSucceeded]) => {
        if (lastSucceeded === true) {
          errorRetryCount = 0;
        }
        if (skippedCount > 0 && isCalculationComplete) {
          if (
            lastSucceeded === true ||
            (lastSucceeded === false && errorRetryCount < MAX_ERROR_RETRIES)
          ) {
            if (lastSucceeded === false) {
              errorRetryCount++;
            }
            void this.refreshState(CalculationRerunPage);
          }
        }
      }),
    );
  }

  private initialiseFilteringOptions(transactions: TransactionView[]) {
    const previousSelected = this.filters.selected();
    const previousOptions = this.filters.options();
    this.filters.initialiseOptions(transactions);

    const currentSelection = this.filters.selected();
    if (
      currentSelection === null ||
      this.filters.allOptionsSelected(previousOptions, previousSelected)
    ) {
      this.filters.updateSelection(this.filters.options());
    } else {
      // Category-option reconciliation (reselect-all-if-was-all, drop-invalid-if-partial,
      // fallback-if-emptied) now lives solely in FilterControl.syncCategoryOptions, which reacts
      // to categoryViews() directly. Duplicating it here raced that effect: whichever ran last
      // won, and the outcome depended on unobservable effect-scheduling order (see #4175 follow-up).
      this.filters.clearDirtySelection(currentSelection);
    }
  }

  protected async updateCalculation() {
    // The balance collections are about to be rewritten, so the previous inputs no longer
    // describe them. Capture this cycle's inputs now (the same values updateBalanceCalculations()
    // reads below) and publish them only once every balance has landed.
    this._completedCalculation.set(null);
    const inputs: CompletedCalculation = {
      transactions: this.calculateTransaction.collection(),
      period: this.period.selected(),
    };

    const calculatedGeneratedTransactions = this.calculateTransaction
      .collection()
      .map((transaction) => transaction.clone());

    // apply the filter period start date to any starting transactions
    calculatedGeneratedTransactions.forEach((transaction) => {
      if (transaction.definition === TransactionStatusEnum.starting_alignment) {
        if (this.period.selected()?.startDate) {
          const startDate = new Date(this.period.selected().startDate);
          transaction.glossPostDate = GlossDateFactory.createFromDate(startDate, true, "00:00:00");
          enrichTransactionDate(transaction);
        }
      }
    });

    calculatedGeneratedTransactions.sort(sortTransactionViews);

    this.filteredTransactions.update(
      this.period
        .filterOnPeriod(this.filterOutSystemTransactions(calculatedGeneratedTransactions))
        .reverse(),
    );

    this.log.info(`Balance Calculation`);
    // A cycle that outlives a clearStore() writes none of its outcome: the store is the next
    // session's now, and clearStore() has already put its status back to idle.
    const isStale = this.staleCheck();
    this.updateBalanceCalculations(calculatedGeneratedTransactions)
      .then(() => {
        if (isStale()) {
          return;
        }
        this.log.info("balances have all been recalculated");
        this._completedCalculation.set(inputs);
        this.storeState$.next({ status: "completed" });
        this._lastCycleSucceeded$.next(true);
      })
      .catch((error) => {
        this.log.error(error);
        if (isStale()) {
          return;
        }
        this.storeState$.next({ status: "error" });
        this._lastCycleSucceeded$.next(false);
      })
      .finally(() => {
        if (isStale()) {
          return;
        }
        this.storeState$.next({ status: "idle" });
        // Mark complete after idle so that subscribers to isCalculationReady$ always see
        // status='idle' when they react — fires for both success and error paths.
        this._isCalculationComplete$.next(true);
      });
  }

  /**
   * Filters out system transaction that we do not want to be displayed in the views, but are required for number accuracy
   * @param transactions
   * @private
   */
  private filterOutSystemTransactions(transactions: TransactionView[]): TransactionView[] {
    return transactions.filter((t) => {
      /** Remove 0 revals **/
      if (t.c_isRevaluation === true) {
        const padded = t.c_valuation?.normalizedValue?.amount?.toFixed(2);
        return padded !== "0.00" && padded !== "-0.00";
      } else {
        return true;
      }
    });
  }

  /**
   * This function is used to recalculate the balances for each balance collection in
   * the calculated store.
   */
  async updateBalanceCalculations(
    calculatedGeneratedTransactions: TransactionView[],
  ): Promise<void> {}

  /**
   * Revaluate the provided transactions
   * This function was moved from the Transaction Store to the Calculation Store because we don't always
   * want to revaluate the transactions. In particular when we are filtered on categories, there will
   * be transactions that will be filtered out that we would have revaluations for which we don't want.
   *
   * We will now move the revaluations to after the filtering step, but we need to make sure if there is
   * filtered on categories that we just skip revalations altogother.
   *
   * TODO: Note it has be agreed upon by Kevin that eventually we will want to revaluate just the allocations
   * that haven't be filtered out. Not the whole transaction value that we currently use.
   * We can address this by introducing a field
   * onto the transaction which is the c_filteredQuantity, which we only sum up the allocations that are not
   * filtered out by the category and classification filters.
   * We then can run a function to normalise this value into a c_filteredNormalisedAmount field.
   * The revaluation class will then need to be updated so that it revaluates based on the c_filteredNormalisedAmount
   * instead of the full transaction amount.
   * Then we will need to update all the places that used to show the normalisedAmounts to now show the
   * c_filteredNormalisedAmount field instead.
   *
   * @param transactions
   * @param granularity cadence to generate reval points at, e.g. previewed from the current period
   */
  async revaluateTransactions(
    transactions: TransactionView[],
    granularity: GranularityProperty,
  ): Promise<void> {
    const revalMetaData = {
      transactionsToReval: transactions,
      referenceData: this.referenceData.referenceDataViews(),
      baseCurrency: this.userStore.preferences.preferenceView().coreCurrency,
      tillDate: new Date(),
      granularity,
    };

    await this.revaluationTransactions.setRevals(revalMetaData);
  }

  /**
   * Determine if we need to apply revaluation transactions to the calculation
   * We should only apply revaluation transactions if there are no category or classification filters applied
   * @private
   */
  private applyRevals(): boolean {
    if (
      (this.filters.selected()?.categories &&
        this.filters.selected().categories.length !== this.filters.options().categories.length) ||
      (this.filters.selected()?.classifications &&
        this.filters.selected().classifications.length !==
          this.filters.options().classifications.length)
    ) {
      this.log.info(
        "Skipping revaluation transactions due to category or classification filters applied",
      );
      return false;
    }
    this.log.info(
      "Applying revaluation transactions because all categories and classifications are selected",
    );
    return true;
  }
}
